import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ref } from 'vue';

import {
  Chart as ChartJS,
  Tooltip,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Legend,
  ChartData,
} from '@/libs/chart.js/dist/chart';
import { Line } from '@/libs/vue-chartjs/dist/index.js';
import { DetailPressure } from '@/Template/OilPressure/OilPressure';
import { formatDateHelper } from '@/helpers/main_helper';

export type Props = {
  rows: DetailPressure[];
  temperatureIndex: number;
  motoIndex: number;
  pressureKey: 'e' | 'o';
  detailKey: 'days' | 'detail';
};

type Point = number | undefined;
type PressureChartData = ChartData<'line', (number | Point | null)[], unknown>;


export default /*#__PURE__*/_defineComponent({
  __name: 'OilPressureDetailChart',
  props: {
    rows: null,
    temperatureIndex: null,
    motoIndex: null,
    pressureKey: null,
    detailKey: null
  },
  setup(__props: any) {

const props = __props as {
  rows: DetailPressure[];
  temperatureIndex: number;
  motoIndex: number;
  pressureKey: 'e' | 'o';
  detailKey: 'days' | 'detail';
}



const getOnlyTime = (val: number) => {
  return formatDateHelper(new Date(+val * 1000), 'hh:nn:ss');
};
const getOnlyDate = (val: number) => {
  return formatDateHelper(new Date(+val * 1000), 'dd.mm.yyyy');
};
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

const chart = ref<any>(null);

const TEMPERATURE: { [key: string]: { label: string; color: string } } = {
  // '0': { label: '< 70', color: '#76ee00' },
  '1': { label: '70 - 75', color: '234, 80, 146' },
  '2': { label: '76 - 80', color: '160, 208, 128' },
  '3': { label: '81 - 85', color: '112, 160, 80' },
  '4': { label: '86 - 90', color: '64, 112, 32' },
  '5': { label: '91 - 95', color: '108, 99, 119' },
  '6': { label: '96 - 100', color: '233, 116, 81' },
  '7': { label: '> 100', color: '0, 0, 38' },
};
const data = computed<PressureChartData>(() => {
  const labels = getTimeLabels();
  const points = getMinMaxDataPoints();

  const datasets = [];
  datasets.push({
    label: `Спрятать все`,
    data: [],
    yAxisID: 'yAxis',
    fill: false,
    showLine: false,
    backgroundColor: 'transparent',
  });

  for (
    let temperatureIndex = 0;
    temperatureIndex < points.length;
    temperatureIndex++
  ) {
    if (
      !points[temperatureIndex][0].length &&
      !points[temperatureIndex][1].length
    ) {
      continue;
    }

    if (!TEMPERATURE[temperatureIndex + '']) {
      continue;
    }

    const { color, label } = TEMPERATURE[temperatureIndex + ''];

    datasets.push({
      label: `Минимальное (${label})`,
      data: points[temperatureIndex][0],
      cubicInterpolationMode: 'monotone',
      yAxisID: 'yAxis',
      fill: false,
      showLine: true,
      borderColor: `rgba(${color},1.0)`,
      backgroundColor: `rgba(${color},0.7)`,
      pointBorderColor: `rgba(${color},1.0)`,
      pointBackgroundColor: `rgba(${color},0.7)`,
      tension: 0.1,
    });
    datasets.push({
      label: `Максимальное (${label})`,
      data: points[temperatureIndex][1],
      cubicInterpolationMode: 'monotone',
      yAxisID: 'yAxis',
      fill: false,
      showLine: true,
      borderColor: `rgba(${color},0.7)`,
      backgroundColor: `rgba(${color},0.7)`,
      pointBorderColor: `rgba(${color},0.7)`,
      pointBackgroundColor: `rgba(${color},0.7)`,
      tension: 0.1,
    });
  }

  return {
    labels,
    datasets: datasets,
  };
});
const options = computed(() => ({
  responsive: true,
  scales: {
    yAxis: {
      min: getYAxisMinValue(),
    },
  },
  plugins: {
    legend: {
      onClick(e: any, legendItem: any, legend: any) {
        const defaultLegendClickHandler =
          ChartJS.defaults.plugins.legend.onClick;

        const index = legendItem.datasetIndex;

        if (index > 0) {
          defaultLegendClickHandler(e, legendItem, legend);
        } else {
          const datasetsLength = Object.keys(TEMPERATURE).length * 2 + 1;
          const isDatasetsVisible = legend.chart.isDatasetVisible(index);
          if (isDatasetsVisible) {
            legend.chart.hide(index);
          } else {
            legend.chart.show(index);
          }
          for (
            let temperatureIndex = 2;
            temperatureIndex < datasetsLength;
            temperatureIndex += 2
          ) {
            if (isDatasetsVisible) {
              legend.chart.hide(temperatureIndex - 1);
              legend.chart.hide(temperatureIndex);
            } else {
              legend.chart.show(temperatureIndex - 1);
              legend.chart.show(temperatureIndex);
            }
          }
        }
      },
    },
  },
}));

const getYAxisMinValue = () => {
  return 0;
};

const getTimeLabels = () => {
  const format = props.detailKey === 'days' ? getOnlyDate : getOnlyTime;
  const labels = props.rows.map((row) => {
    return format(row.time);
  });

  return labels;
};

function getMinMaxDataPoints(): Point[][][] {
  const temperaturePoints: Point[][][] = [];

  for (const row of props.rows) {
    const pressure = row?.pressure?.[props.pressureKey] || {};

    const moto = pressure?.[props.motoIndex] || {};

    for (const temperatureIndex in moto) {
      const [min, max] = moto[temperatureIndex];

      const Min = min > -1 ? min : undefined;
      const Max = max > -1 ? max : undefined;

      if (!temperaturePoints[+temperatureIndex]) {
        temperaturePoints[+temperatureIndex] = [[], []];
      }

      temperaturePoints[+temperatureIndex][0].push(Min);
      temperaturePoints[+temperatureIndex][1].push(Max);
    }
  }

  return temperaturePoints;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Line), {
    ref_key: "chart",
    ref: chart,
    data: _unref(data),
    options: _unref(options),
    "data-moto": props.motoIndex,
    "data-temperature": props.temperatureIndex
  }, null, 8, ["data", "options", "data-moto", "data-temperature"]))
}
}

})